import type { Role } from '../constants/roles';
import { ROLES } from '../constants/roles';

/** Allowlisted assistant navigation / tour actions — never accept raw LLM targets. */
export interface AssistantActionDef {
  id: string;
  /** Deep-link target consumed by AppShell.navigate, e.g. banker:config */
  target: string;
  roles: Role[];
  labels: { en: string; es: string };
}

export const ASSISTANT_ACTIONS: AssistantActionDef[] = [
  {
    id: 'open_config',
    target: 'banker:config',
    roles: [ROLES.BANKER, ROLES.GOD],
    labels: { en: 'Open Config', es: 'Abrir Config' },
  },
  {
    id: 'open_plays',
    target: 'banker:plays',
    roles: [ROLES.BANKER],
    labels: { en: 'Open Plays', es: 'Abrir Jugadas' },
  },
  {
    id: 'open_users',
    target: 'banker:users',
    roles: [ROLES.BANKER, ROLES.GOD],
    labels: { en: 'Open Users', es: 'Abrir Usuarios' },
  },
  {
    id: 'open_overview',
    target: 'banker:overview',
    roles: [ROLES.BANKER],
    labels: { en: 'Open Overview', es: 'Abrir Resumen' },
  },
  {
    id: 'open_newplay',
    target: 'lister:newplay',
    roles: [ROLES.LISTER],
    labels: { en: 'New play', es: 'Nueva jugada' },
  },
  {
    id: 'open_lister_plays',
    target: 'lister:plays',
    roles: [ROLES.LISTER],
    labels: { en: 'My plays', es: 'Mis jugadas' },
  },
  {
    id: 'open_results',
    target: 'lister:results',
    roles: [ROLES.LISTER],
    labels: { en: 'Open Results', es: 'Abrir Resultados' },
  },
  {
    id: 'open_route',
    target: 'runner:route',
    roles: [ROLES.RUNNER],
    labels: { en: 'Open Route', es: 'Abrir Ruta' },
  },
  {
    id: 'open_banker_route',
    target: 'banker:route',
    roles: [ROLES.BANKER],
    labels: { en: 'Open Route', es: 'Abrir Ruta' },
  },
  {
    id: 'open_handoff',
    target: 'lister:handoff',
    roles: [ROLES.LISTER],
    labels: { en: 'Confirm handoff', es: 'Confirmar entrega' },
  },
  {
    id: 'open_history',
    target: 'runner:history',
    roles: [ROLES.RUNNER],
    labels: { en: 'Open History', es: 'Abrir Historial' },
  },
  {
    id: 'open_tickets',
    target: 'support_agent:tickets',
    roles: [ROLES.SUPPORT_AGENT, ROLES.GOD],
    labels: { en: 'Open Tickets', es: 'Abrir Tickets' },
  },
  {
    id: 'open_knowledge',
    target: 'support_agent:knowledge',
    roles: [ROLES.SUPPORT_AGENT, ROLES.GOD],
    labels: { en: 'Open Knowledge', es: 'Abrir Conocimiento' },
  },
  {
    id: 'open_houses',
    target: 'god:houses',
    roles: [ROLES.GOD],
    labels: { en: 'Open Houses', es: 'Abrir Bancas' },
  },
  {
    id: 'open_integrity',
    target: 'god:integrity',
    roles: [ROLES.GOD],
    labels: { en: 'Open Integrity', es: 'Abrir Integridad' },
  },
  {
    id: 'open_rates',
    target: 'banker:rates',
    roles: [ROLES.BANKER, ROLES.LISTER, ROLES.GOD],
    labels: { en: 'Exchange rates', es: 'Tasas de cambio' },
  },
];

const BY_ID = new Map(ASSISTANT_ACTIONS.map((a) => [a.id, a]));

/** Topic id → preferred action id for navigation chips. */
export const TOPIC_ACTION_IDS: Record<string, string> = {
  'caps-banker': 'open_config',
  'cutoff-banker': 'open_config',
  'results-banker': 'open_overview',
  'plays-banker': 'open_plays',
  'routes-banker': 'open_banker_route',
  'users-banker': 'open_users',
  'caps-lister': 'open_newplay',
  'cutoff-lister': 'open_newplay',
  'plays-lister': 'open_newplay',
  'results-lister': 'open_results',
  'handoff-lister': 'open_handoff',
  'cutoff-runner': 'open_route',
  'routes-runner': 'open_route',
  'history-runner': 'open_history',
  'tickets-support': 'open_tickets',
  'knowledge-support': 'open_knowledge',
  'houses-god': 'open_houses',
  'integrity-god': 'open_integrity',
};

export function resolveAssistantAction(
  actionId: string,
  role?: string,
  lang: 'en' | 'es' = 'es',
): { id: string; target: string; label: string } | null {
  const def = BY_ID.get(actionId);
  if (!def) return null;
  if (role && !def.roles.includes(role as Role)) return null;
  // God browsing a house may still open banker-scoped targets via navigate tab parsing.
  return { id: def.id, target: def.target, label: def.labels[lang] };
}

export function actionsForRole(role?: string, lang: 'en' | 'es' = 'es') {
  if (!role) return [];
  return ASSISTANT_ACTIONS
    .filter((a) => a.roles.includes(role as Role))
    .map((a) => ({ id: a.id, target: a.target, label: a.labels[lang] }));
}
